import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, Switch, message, Card } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { get, post } from "../../utils/http";

interface ModuleData {
  id: number;
  module_name: string;
  module_name_en: string;
  module_desc: string;
  is_active: boolean;
  create_time: string;
  update_time: string;
}

const ModulePage: React.FC = () => {
  const [modules, setModules] = useState<ModuleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingModule, setEditingModule] = useState<ModuleData | null>(null);

  // 获取模块列表
  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await get("/api/manage/module-list");
      if (res.data.code === 0) {
        // 确保data是数组类型
        const data = res.data.data.list;
        setModules(Array.isArray(data) ? data : []);
      } else {
        message.error(res.data.message || "获取模块列表失败");
      }
    } catch (error) {
      message.error("获取模块列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  // 创建或编辑模块
  const handleSave = async (values: any) => {
    try {
      let res;
      if (editingModule) {
        // 编辑模块
        res = await post("/api/manage/module-update", {
          id: editingModule.id,
          ...values,
        });
        if (res.data.code === 0) {
          message.success("编辑模块成功");
        }
      } else {
        // 创建模块
        res = await post("/api/manage/module-create", values);
        if (res.data.code === 0) {
          message.success("创建模块成功");
        }
      }

      if (res.data.code === 0) {
        setModalVisible(false);
        form.resetFields();
        setEditingModule(null);
        fetchModules();
      } else {
        message.error(res.data.message || "操作失败");
      }
    } catch (error) {
      message.error(editingModule ? "编辑模块失败" : "创建模块失败");
    }
  };

  // 编辑模块
  const handleEdit = (record: ModuleData) => {
    setEditingModule(record);
    form.setFieldsValue({
      module_name: record.module_name,
      module_name_en: record.module_name_en,
      module_desc: record.module_desc,
    });
    setModalVisible(true);
  };

  // 删除模块
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "确认删除",
      content: "确定要删除该模块吗？",
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        try {
          const res = await post("/api/manage/module-delete", { id });
          if (res.data.code === 0) {
            message.success("删除模块成功");
            fetchModules();
          } else {
            message.error(res.data.errorMsg || "删除模块失败");
          }
        } catch (error) {
          message.error("删除模块失败");
        }
      },
    });
  };

  // 切换上/下架状态
  const handleToggleStatus = async (id: number, isActive: boolean) => {
    try {
      const res = await post("/api/manage/module-toggle-status", {
        id,
        is_active: isActive,
      });
      if (res.data.code === 0) {
        message.success(isActive ? "上架成功" : "下架成功");
        fetchModules();
      } else {
        message.error(res.data.message || "操作失败");
      }
    } catch (error) {
      message.error("操作失败");
    }
  };

  const columns = [
    {
      title: "模块名称",
      dataIndex: "module_name",
      key: "module_name",
    },
    {
      title: "翻译",
      dataIndex: "module_name_en",
      key: "module_name_en",
    },
    {
      title: "模块描述",
      dataIndex: "module_desc",
      key: "module_desc",
    },
    {
      title: "创建时间",
      dataIndex: "create_time",
      key: "create_time",
      render: (text: string) => {
        if (!text) return "-";
        return new Date(text).toLocaleString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      },
    },
    {
      title: "更新时间",
      dataIndex: "update_time",
      key: "update_time",
      render: (text: string) => {
        if (!text) return "-";
        return new Date(text).toLocaleString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      },
    },
    {
      title: "状态",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive: boolean, record: ModuleData) => (
        <Switch
          checked={isActive}
          checkedChildren="上架"
          unCheckedChildren="下架"
          onChange={(checked) => handleToggleStatus(record.id, checked)}
        />
      ),
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: ModuleData) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          >
            编辑
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        title="模块管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            新建模块
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={modules}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
          }}
        />
      </Card>

      <Modal
        title={editingModule ? "编辑模块" : "新建模块"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingModule(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="module_name"
            label="模块名称"
            rules={[{ required: true, message: "请输入模块名称" }]}
          >
            <Input placeholder="请输入模块名称" />
          </Form.Item>

          <Form.Item name="module_name_en" label="翻译">
            <Input placeholder="请输入模块英文翻译" />
          </Form.Item>

          <Form.Item name="module_desc" label="模块描述" rules={[]}>
            <Input.TextArea placeholder="请输入模块描述" rows={4} />
          </Form.Item>

          <Form.Item style={{ textAlign: "right" }}>
            <Button onClick={() => setModalVisible(false)}>取消</Button>
            <Button type="primary" htmlType="submit" style={{ marginLeft: 8 }}>
              确定
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModulePage;
